/**
 * AI Generation Example Prompts
 * 
 * A comprehensive list of example prompts for the AI music generator.
 * Used in both mobile and desktop AI generate interfaces.
 * Each entry has a short label (for the chip button) and a full prompt.
 */

export interface AIExamplePrompt {
  /** Short label shown on the chip button */
  label: string;
  /** Full prompt text inserted into the textarea */
  prompt: string;
  /** Optional emoji/icon to display before the label */
  emoji?: string;
}

export const AI_EXAMPLE_PROMPTS: AIExamplePrompt[] = [
  // Battle & Action
  { emoji: '⚔️', label: 'Battle Theme', prompt: 'upbeat battle theme with fast drums and intense square lead' },
  { emoji: '👹', label: 'Boss Fight', prompt: 'epic boss fight music with heavy bass and dramatic tempo changes' },
  { emoji: '🏃', label: 'Chase Scene', prompt: 'fast-paced chase music at 160 BPM with urgent arpeggios' },
  { emoji: '💀', label: 'Final Boss', prompt: 'intense final boss theme in minor key with layered melodies' },
  { emoji: '🚀', label: 'Space Battle', prompt: 'sci-fi space battle theme with laser SFX and pulsing bass' },

  // Exploration & Adventure
  { emoji: '🏘️', label: 'Village Music', prompt: 'peaceful village music with soft triangle melody and gentle rhythm' },
  { emoji: '🗝️', label: 'Dungeon Theme', prompt: 'mysterious dungeon theme with dark tones and echoing notes' },
  { emoji: '🗺️', label: 'Overworld Map', prompt: 'adventurous overworld map theme with a heroic melody' },
  { emoji: '🌲', label: 'Forest Walk', prompt: 'calm forest exploration music with nature-inspired sounds' },
  { emoji: '⛵', label: 'Ocean Voyage', prompt: 'sailing adventure theme with rolling waves feel and bright melody' },
  { emoji: '⛰️', label: 'Mountain Climb', prompt: 'ascending mountain theme that builds in intensity and height' },
  { emoji: '🏜️', label: 'Desert Oasis', prompt: 'exotic desert theme with Middle Eastern scale and shimmering pads' },

  // Mood & Atmosphere
  { emoji: '😢', label: 'Sad Piano', prompt: 'melancholic slow melody in minor key with soft pads' },
  { emoji: '🏆', label: 'Victory Fanfare', prompt: 'triumphant short victory jingle with bright major chords' },
  { emoji: '💔', label: 'Game Over', prompt: 'short sad game over theme with descending notes' },
  { emoji: '🎮', label: 'Title Screen', prompt: 'catchy title screen music that loops well with a memorable hook' },
  { emoji: '🎬', label: 'Credits Roll', prompt: 'uplifting credits theme that recaps the adventure with warm tones' },
  { emoji: '😰', label: 'Suspense', prompt: 'tense suspenseful music with minimal notes and building dread' },
  { emoji: '🔍', label: 'Mystery', prompt: 'mysterious detective theme with jazzy undertones and staccato notes' },

  // Genre-Specific
  { emoji: '🦘', label: 'Platformer', prompt: 'bouncy platformer music at 140 BPM with fun jumping feel' },
  { emoji: '🏎️', label: 'Racing Game', prompt: 'high-energy racing theme at 170 BPM with driving bass line' },
  { emoji: '🧩', label: 'Puzzle Game', prompt: 'relaxing puzzle game music with gentle repeating patterns' },
  { emoji: '👻', label: 'Horror Game', prompt: 'creepy horror game ambience with dissonant chords and tension' },
  { emoji: '🏰', label: 'RPG Town', prompt: 'cozy RPG town theme with warm chords and friendly melody' },
  { emoji: '🥷', label: 'Stealth Level', prompt: 'sneaky stealth music with quiet bass and sparse percussion' },
  { emoji: '🕹️', label: 'Arcade Classic', prompt: 'classic arcade game music with catchy hook and fast tempo' },
  { emoji: '🎵', label: 'Rhythm Game', prompt: 'funky rhythm game track with syncopated beats and groovy bass' },

  // Style & Era
  { emoji: '📺', label: 'NES Style', prompt: 'authentic NES-style music with square and triangle waves only' },
  { emoji: '🎮', label: 'Game Boy', prompt: 'Game Boy style music with limited channels and lo-fi charm' },
  { emoji: '🌆', label: 'Synthwave', prompt: 'retro synthwave track with arpeggiated chords and pulsing bass' },
  { emoji: '🎤', label: 'Chiptune Pop', prompt: 'upbeat chiptune pop song with catchy chorus melody' },
  { emoji: '☕', label: 'Lo-Fi Chill', prompt: 'chill lo-fi chiptune beat with relaxed tempo and warm pads' },
  { emoji: '🎺', label: 'Funk Groove', prompt: 'funky 8-bit groove with slap bass and rhythmic percussion' },

  // Specific Moods
  { emoji: '🎉', label: 'Celebration', prompt: 'joyful celebration music with fast arpeggios and bright tones' },
  { emoji: '🌙', label: 'Lullaby', prompt: 'gentle lullaby with slow tempo and soothing triangle melody' },
  { emoji: '🎺', label: 'Epic March', prompt: 'epic marching theme with steady drums and powerful brass-like leads' },
  { emoji: '🌊', label: 'Underwater', prompt: 'dreamy underwater level music with bubbly effects and flowing melody' },
  { emoji: '🏚️', label: 'Haunted House', prompt: 'spooky haunted house theme with eerie high notes and creaking bass' },
  { emoji: '🎪', label: 'Carnival', prompt: 'playful carnival music with bouncy rhythm and whimsical melody' },
  { emoji: '🥷', label: 'Ninja Level', prompt: 'fast ninja action theme with sharp staccato notes and quick drums' },
  { emoji: '❄️', label: 'Ice World', prompt: 'sparkling ice world theme with crystalline arpeggios and cool pads' },
];

/**
 * Get a random selection of example prompts.
 * Uses a simple shuffle to pick `count` unique examples.
 */
export function getRandomExamples(count: number = 3): AIExamplePrompt[] {
  const shuffled = [...AI_EXAMPLE_PROMPTS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}
